import React, {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import classnames from 'classnames'
import {
    Button,
    Card,
    CardBody,
    Input,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Spinner,
    Row,
    Col,
    Form
} from 'reactstrap'
import * as ICON from 'react-feather'
import {
    getFavFolderList,
    createFavFolder,
    createFavFolderReset,
    addToFavFolder,
    addToFavFolderReset,
    updateFavFolder,
    updateFavFolderReset
} from '@store/actions/BrandActions'
import {useFormik} from 'formik'
import * as yup from 'yup'
import {successAlert, errorAlert} from '@src/views/components/CustomAlerts'

const AddToFav = ({isOpen, onClose, type, data = null, onSuccess, hideListing = false, onFolderCreate, title = 'Add to favorite', folderData = null}) => {
    const [selectedFolder, setSelectedFolder] = useState(null)
    const dispatch = useDispatch()

    const {
        favFolderListLoading,
        favFolderListData,
        favFolderListError,

        favFolderCreateLoading,
        favFolderCreateData,
        favFolderCreateError,

        addToFavFolderLoading,
        addToFavFolderData,
        addToFavFolderError,

        favFolderUpdateLoading,
        favFolderUpdateData,
        favFolderUpdateError
    } = useSelector(state => state.brandReducer)

    const handleError = (error) => {
        if (error?.response?.status) {
            errorAlert(error?.response?.data?.message || 'Bad Request')
        } else {
            errorAlert(error?.msg || 'Something went wrong')
        }
        dispatch(createFavFolderReset())
        dispatch(createFavFolderReset())
        dispatch(updateFavFolderReset())
    }

    const form = useFormik({
        initialValues: {
            id: '',
            name: ''
        },
        validationSchema: yup.object().shape({
            name: yup.string().required('Folder name is required')
        }),
        onSubmit: (values) => {
            setSelectedFolder(null)
            if (folderData) {
                dispatch(updateFavFolder({
                    id: values?.id,
                    folder_name: values?.name
                }))
            } else {
                dispatch(createFavFolder({
                    folder_name: values.name
                }))
            }
        }
    })

    useEffect(() => {
        if (hideListing === false) {
            dispatch(getFavFolderList({
                list: 'all'
            }))
        }
    }, [])

    useEffect(() => {
        if (favFolderCreateData?.status === true) {
            if (hideListing === false) {
                dispatch(getFavFolderList({
                    list: 'all'
                }))
            }
            if (hideListing === true) {
                onFolderCreate()
                onClose()
            }
            successAlert(favFolderCreateData?.msg)
            dispatch(createFavFolderReset())
            form.resetForm()
        }
        if (favFolderUpdateData?.status === true) {
            successAlert(favFolderUpdateData?.msg)
            dispatch(updateFavFolderReset())
            onClose()
            onFolderCreate()
            form.resetForm()
        }
        if (addToFavFolderData?.success === true) {
            successAlert(addToFavFolderData?.msg)
            dispatch(addToFavFolderReset())
            form.resetForm()
            setSelectedFolder(null)
            onSuccess()
        }
        if (favFolderCreateData?.status === false) {
            errorAlert(favFolderCreateData?.msg)
            dispatch(createFavFolderReset())
        }
        if (favFolderListError) {
            handleError(favFolderListError)
        }
        if (favFolderCreateError) {
            handleError(favFolderCreateError)
        }
        if (addToFavFolderError) {
            handleError(addToFavFolderError)
        }
        if (favFolderUpdateError) {
            handleError(favFolderUpdateError)
        }
    }, [
        favFolderCreateData,
        addToFavFolderData,
        favFolderListError,
        favFolderCreateError,
        addToFavFolderError,
        favFolderUpdateData,
        favFolderUpdateError
    ])

    useEffect(() => {
        if (folderData) {
            form.setFieldValue('id', folderData?.id)
            form.setFieldValue('name', folderData?.folder_name)
        }
    }, [folderData])

    const handleSelectFolder = (item) => {
        setSelectedFolder(item)
        form.resetForm()
    }

    const handleAddToFolder = () => {
        form.resetForm()
        if (type === 'RD_PROFILE') {
            dispatch(addToFavFolder(
                {
                    id: data?.id,
                    folder_id: selectedFolder?.id,
                    type: 'profile'
                }
            ))
        }
    }

    return (
        <Modal isOpen={isOpen} size='lg' scrollable={true}>
            <ModalHeader toggle={() => {
                setSelectedFolder(null)
                form.resetForm()
                onClose()
            }}>
                {title}
            </ModalHeader>
            <ModalBody style={{minHeight: '0'}}>
                <p className="font-weight-bold bg-light p-1 text-primary">
                    <ICON.PlusCircle size={16}/> Create New Folder
                </p>
                <Form onSubmit={form.handleSubmit} autoComplete='off'>
                    <div className='form-group d-flex'>
                        <Input
                            type='text'
                            name='name'
                            id='name'
                            placeholder='Folder Name'
                            value={form.values.name}
                            onBlur={form.handleBlur}
                            onChange={form.handleChange}
                            className={classnames({'is-invalid': form.touched.name && form.errors.name})}
                            disabled={favFolderCreateLoading || favFolderUpdateLoading}
                        />
                        <Button
                            color='primary'
                            disabled={favFolderCreateLoading || favFolderUpdateLoading}
                            type='submit'
                            className='ml-1 d-flex align-items-center'>
                            {(favFolderCreateLoading || favFolderUpdateLoading) ? (
                                <Spinner size='sm' color='white'/>
                            ) : (
                                <>
                                    {folderData ? 'Update' : 'Create'}
                                </>
                            )}
                        </Button>
                    </div>
                </Form>
                {hideListing === false && (
                    favFolderListLoading ? (
                        <div className="w-100 my-4 text-center"><Spinner color='primary'/></div>
                    ) : (favFolderListData?.data?.length > 0 && (
                            <>
                                <p className="font-weight-bold bg-light p-1 text-primary">
                                    <ICON.ArrowDownCircle size={16}/> Select Existing Folder
                                </p>
                                <Row>
                                    {favFolderListData?.data?.map((item, index) => (
                                        <Col md={6} key={`fav-folder-${index}`}>
                                            <Card
                                                className={classnames({
                                                    'mb-1': true,
                                                    'border-1px-white': selectedFolder?.id !== item.id,
                                                    'border-1px-purple': selectedFolder?.id === item.id
                                                })}
                                            >
                                                <CardBody>
                                                    <Button
                                                        className='p-0'
                                                        color='link'
                                                        onClick={() => handleSelectFolder(item)}
                                                        disabled={addToFavFolderLoading}
                                                    >
                                                        <ICON.Folder size={18} className='mr-1'/> {item.folder_name}
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            </>
                        )
                    ))}
            </ModalBody>
            <ModalFooter className='d-flex justify-content-end'>
                {hideListing === false && favFolderListData?.data?.length > 0 && (
                    <Button
                        color='primary'
                        disabled={!selectedFolder || addToFavFolderLoading}
                        type='button'
                        onClick={handleAddToFolder}
                    >
                        {addToFavFolderLoading ? (
                            <Spinner size='sm' color='white'/>
                        ) : (
                            <>
                                Add
                            </>
                        )}
                    </Button>
                )}
            </ModalFooter>
        </Modal>
    )
}

export default AddToFav